<template>
    <div class="input-container mt-3">
        <div class="row">
            <div class="col-md-2">
                <InputField label="Select Database" type="select" v-model="database" :editable="true" :options="databaseOptions" @update:model-value="handleDatabaseChange" />
            </div>
            <div class="col-md-2" v-if="database">
                <div v-if="tablesLoading" class="text-center">
                    <div class="spinner-border" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
                <div v-else>
                    <InputField label="Select Table" type="select" v-model="table" :editable="true" :options="tableOptions" :disabled="!database" />
                </div>
            </div>
            <div class="col-md-2 align-self-center">
                <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" id="searchModeSwitch" v-model="useEntityIdSearch">
                    <label class="form-check-label" for="searchModeSwitch">
                        Search by Entity ID
                    </label>
                </div>
            </div>
            <!-- Entity ID or Limit field -->
            <div class="col-md-2">
                <InputField 
                    v-if="useEntityIdSearch" 
                    label="Entity ID" 
                    type="text" 
                    v-model="entityId" 
                    :editable="true" 
                    placeholder="Enter entity ID" 
                />
                <InputField 
                    v-else 
                    label="Limit" 
                    type="number" 
                    v-model="limit" 
                    :editable="true" 
                />
            </div>
            <div class="col-md-2">
                <InputField label="Depth" type="number" v-model="depth" :editable="true" />
            </div>
            <div class="col-md-2 align-self-center">
                <button class="btn btn-primary w-100" @click="updateGraph" :disabled="loading || !canUpdate">
                    <div v-if="loading" class="spinner-border spinner-border-sm" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <div v-else>
                        <i class="bi bi-search me-2"></i>
                        Update
                    </div>
                </button>
            </div>
        </div>

    </div>
    <div class="card">
        <div ref="graphContainer" class="olap-graph-container d-flex justify-content-center"></div>
    </div>
</template>

<script>
import * as d3 from "d3"
import axios from "axios"
import { mapActions, mapGetters } from 'vuex'
import InputField from "./InputField.vue"

export default {
    components: { 
        InputField,
    },
    data() {
        return {
            database: "",
            table: "",
            depth: 2,
            limit: 10,
            entityId: "",
            useEntityIdSearch: false,
            loading: false,
            tablesLoading: false,
            databaseOptions: [],
            tableOptions: [],
            nodeLevel: 1,
            simulation: null,
            svg: null
        }
    },

    computed: {
        ...mapGetters('idr', ['getNetworkData']),
        networkData() {
            return this.getNetworkData;
        },
        canUpdate() {
            // Database and table are always required
            if (!this.database || !this.table) {
                return false;
            }
            
            // If using entity ID search, entity ID is required
            if (this.useEntityIdSearch) {
                return this.entityId && this.entityId.trim() !== '';
            }
            
            // For random sampling, limit and depth should be valid numbers
            return this.limit > 0 && this.depth > 0;
        },
        graphQuery() {
            let query = `WITH RECURSIVE hierarchy(entity_type,entity_domain,entity_id,entity_neighbor_type,entity_neighbor_domain,entity_neighbors,depth) AS (
                SELECT
                    t.entity_type,
                    t.entity_domain,
                    t.entity_id,
                    t.entity_neighbor_type,
                    t.entity_neighbor_domain,
                    t.entity_neighbors,
                    0 AS depth
                FROM "${this.database}"."${this.table}" AS t
                `
            
            // Choose between entity ID search or random sampling
            if (this.useEntityIdSearch) {
                query += `
                    WHERE t.entity_id = '${this.entityId}'
                    `
            } else {
                query += `
                    WHERE t.entity_id IN (SELECT entity_id FROM "${this.database}"."${this.table}" ORDER BY RANDOM() LIMIT ${this.limit})
                    `
            }

            query += `
                UNION ALL

                SELECT
                    child.entity_type,
                    child.entity_domain,
                    child.entity_id,
                    child.entity_neighbor_type,
                    child.entity_neighbor_domain,
                    child.entity_neighbors,
                    parent.depth + 1 AS depth
                FROM hierarchy AS parent
                    -- unnest the parent's relationships
                    JOIN LATERAL (
                        SELECT r.id
                        FROM UNNEST(parent.entity_neighbors) AS t(r)
                    ) AS rel
                    ON TRUE
                    -- join to the table on the relationship id
                    JOIN "${this.database}"."${this.table}" AS child
                    ON child.entity_id = rel.id
                        AND child.entity_type   = parent.entity_type
                        AND child.entity_domain = parent.entity_domain
                WHERE parent.depth < ${this.depth}
            )
            SELECT DISTINCT
                entity_type, 
                entity_domain, 
                entity_id,
                entity_neighbor_type,
                entity_neighbor_domain,
                n.id AS neighbor_entity_id
            FROM hierarchy, UNNEST(entity_neighbors) AS t(n) 
            ORDER BY entity_type, entity_domain, entity_id, entity_neighbor_type, entity_neighbor_domain;
            `
            return query
        }
    },

    methods: {
        ...mapActions({
            fetchAndConvertNetworkData: "idr/fetchAndConvertNetworkData",
        }),

        async getDatabases() {
            try {
                const response = await axios.get(
                    `${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${this.$store.state.currentClient.cid}/dbs`,
                    {
                        headers: {
                            Authorization: `Bearer ${this.$store.state.accessToken}`,
                        },
                    }
                );
                
                if (response.data && Array.isArray(response.data)) {
                    this.databaseOptions = response.data.map(db => ({
                        label: db,
                        value: db
                    }));
                }
            } catch (error) {
                console.error('Error fetching databases:', error);
            }
        },

        async handleDatabaseChange(newValue) {
            this.table = "";
            this.tableOptions = [];
            this.tablesLoading = true;

            if (!newValue) return;
            
            try {
                const response = await axios.get(
                    `${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${this.$store.state.currentClient.cid}/dbs/${newValue}/tables`,
                    {
                        headers: {
                            Authorization: `Bearer ${this.$store.state.accessToken}`,
                        },
                    }
                );
                
                if (response.data) {
                    let tables = response.data.tables
                    if (response.data.views) {
                        tables = tables.concat(response.data.views)
                    }
                    this.tableOptions = tables.map(table => ({
                        label: table,
                        value: table
                    }));
                }
            } catch (error) {
                console.error('Error fetching tables:', error);
            } finally {
                this.tablesLoading = false;
            }
        },

        handleEnter() {
            if (this.canUpdate) {
                this.updateGraph();
            }
        },

        async updateGraph() {
            this.loading = true;
            try {
                await this.initNetwork()
            } catch (error) {
                console.error('Search failed:', error)
            } finally {
                this.loading = false;
            }
        },
        async fetchAndConvertOlapData() {
            try {
                // Step 1: Initiate the query execution
                const response = await axios.post(
                    `${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${this.$store.state.currentClient.cid}/query_executions`,
                    {
                        query: this.graphQuery
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${this.$store.state.accessToken}`,
                            'Content-Type': 'application/json'
                        },
                    }
                );

                const queryExecutionId = response.data?.query_execution_id;
                if (!queryExecutionId) {
                    throw new Error("No query_execution_id in response");
                }

                // Step 2: Wait initial delay (2 seconds)
                await this.sleep(1000);

                // Step 3: Poll for status with maximum retries
                const maxRetries = 60; // Maximum number of polls (5 minutes with 5-second intervals)
                const delay = 1000; // 5 seconds between polls

                for (let i = 0; i < maxRetries; i++) {
                    const statusResponse = await axios.get(
                        `${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${this.$store.state.currentClient.cid}/query_status/${queryExecutionId}`,
                        {
                            headers: {
                                Authorization: `Bearer ${this.$store.state.accessToken}`,
                            },
                        }
                    );

                    const statusData = statusResponse.data;

                    // Check if query failed
                    if (statusData.State === 'FAILED') {
                        throw new Error(statusData.StateChangeReason || 'Query failed');
                    }

                    // Check if query is complete (not QUEUED or RUNNING)
                    if (statusData.State && statusData.State !== 'QUEUED' && statusData.State !== 'RUNNING') {
                        // Step 4: Get final results
                        const resultsResponse = await axios.get(
                            `${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${this.$store.state.currentClient.cid}/query_executions/${queryExecutionId}`,
                            {
                                headers: {
                                    Authorization: `Bearer ${this.$store.state.accessToken}`,
                                },
                            }
                        );

                        // Process and convert the results to network data format
                        return this.processQueryResults(resultsResponse.data);
                    }

                    // Wait before next poll
                    await this.sleep(delay);
                }

                throw new Error("Query timed out after maximum retries");

            } catch (error) {
                console.error('Error in fetchAndConvertOlapData:', error);
                throw error;
            }
        },

        processQueryResults(queryData) {
            // Process the query results and convert to network format
            // This function should convert the OLAP query results to nodes and edges
            const nodes = [];
            const edges = [];

            if (queryData.data && queryData.column_info) {
                // Process each row of data
                queryData.data.forEach(row => {
                    const rowObj = {};
                    // Map column values to column names
                    for (let i = 0; i < queryData.column_info.length; i++) {
                        rowObj[queryData.column_info[i].name] = row[i];
                    }

                    // Extract nodes and edges based on the query structure
                    // This assumes the query returns entity information and neighbor relationships
                    if (rowObj.entity_id && rowObj.entity_type && rowObj.entity_domain) {
                        const nodeId = `${rowObj.entity_type}~${rowObj.entity_domain}~${rowObj.entity_id}`;
                        
                        // Add source node if not already present
                        if (!nodes.find(n => n.id === nodeId)) {
                            nodes.push({
                                id: nodeId,
                                type: rowObj.entity_type,
                                domain: rowObj.entity_domain,
                                key: rowObj.entity_id
                            });
                        }

                        // Add neighbor node and edge if neighbor data exists
                        if (rowObj.neighbor_entity_id && rowObj.entity_neighbor_type && rowObj.entity_neighbor_domain) {
                            const neighborId = `${rowObj.entity_neighbor_type}~${rowObj.entity_neighbor_domain}~${rowObj.neighbor_entity_id}`;
                            
                            // Add neighbor node if not already present
                            if (!nodes.find(n => n.id === neighborId)) {
                                nodes.push({
                                    id: neighborId,
                                    type: rowObj.entity_neighbor_type,
                                    domain: rowObj.entity_neighbor_domain,
                                    key: rowObj.neighbor_entity_id
                                });
                            }

                            // Add edge if not already present
                            if (!edges.find(e => e.from === nodeId && e.to === neighborId)) {
                                edges.push({
                                    from: nodeId,
                                    to: neighborId,
                                    fromType: rowObj.entity_type,
                                    toType: rowObj.entity_neighbor_type
                                });
                            }
                        }
                    }
                });
            }

            return { nodes, edges };
        },

        sleep(time) {
            return new Promise(function (resolve) {
                setTimeout(resolve, time);
            });
        },

        async initNetwork() {
            try {
                // Fetch and convert the OLAP data
                const networkData = await this.fetchAndConvertOlapData();
                
                // Store the network data in the Vuex store using the same pattern as OLTP
                this.$store.commit('idr/setNetworkData', networkData);

                const container = this.$refs.graphContainer;
                if (!container) {
                    console.error('Network container not found')
                    return
                }

                if (!networkData || !networkData.nodes || !networkData.edges) {
                    console.error('Invalid network data:', networkData)
                    return
                }
                console.log('Initializing network with data:', networkData)

                let graph = {
                    nodes: [],
                    edges: [],
                }
                networkData.nodes.forEach(node => {
                    graph.nodes.push({
                        id: node.id,
                        type: node.type,
                        domain: node.domain,
                        key: node.key
                    });
                });
                networkData.edges.forEach(edge => {
                    graph.edges.push({
                        source: edge.from,
                        target: edge.to
                    });
                });
                this.drawGraph(graph);
            } catch (error) {
                console.error('Error in initNetwork:', error);
            }
        },

        drawGraph(data) {
            const width = this.$refs.graphContainer.clientWidth;
            const height = 600;

            // Remove old SVG if it exists
            d3.select(this.$refs.graphContainer).select("svg").remove();

            // Create new SVG with zoom/pan support
            const svg = d3.select(this.$refs.graphContainer)
                .append("svg")
                .attr("width", width)
                .attr("height", height)
                .call(
                    d3.zoom()
                        .scaleExtent([0.5, 5]) // Min zoom 50%, max zoom 500%
                        .on("zoom", (event) => {
                            g.attr("transform", event.transform);
                        })
                );

            // Add a group element for panning/zooming
            const g = svg.append("g");
            this.simulation = d3.forceSimulation(data.nodes)
                .force("link", d3.forceLink(data.edges).id(d => d.id).distance(30).strength(1.0))
                .force("charge", d3.forceManyBody().strength(-40))
                .force("center", d3.forceCenter(width / 2, height / 2).strength(0.15))
                .force("collision", d3.forceCollide().radius(15))
                .force("x", d3.forceX(width / 2).strength(0.05))
                .force("y", d3.forceY(height / 2).strength(0.05))
                .velocityDecay(0.7)
                .alphaDecay(0.01);

            // Create edges (links)
            const link = g.selectAll(".link")
                .data(data.edges)
                .enter()
                .append("line")
                .attr("stroke", "#999")
                .attr("stroke-width", 1.5)
                .attr("stroke-opacity", 0.6);

            // Create node groups
            const node = g.selectAll(".node")
                .data(data.nodes)
                .enter()
                .append("g")
                .attr("class", "node")
                .call(
                    d3.drag()
                        .on("start", (event, d) => {
                            // Gently restart simulation with very low alpha to minimize disturbance
                            if (!event.active) this.simulation.alphaTarget(0.01).restart();
                            d.fx = d.x;
                            d.fy = d.y;
                        })
                        .on("drag", (event, d) => {
                            // Update the dragged node's position
                            d.fx = event.x;
                            d.fy = event.y;
                        })
                        .on("end", (event, d) => {
                            // Stop the gentle simulation restart
                            if (!event.active) this.simulation.alphaTarget(0);
                            d.fx = null;
                            d.fy = null;
                        })
                );

            // Add circles for nodes
            node.append("circle")
                .attr("stroke", "white") // Border color
                .attr("stroke-width", 1.5) // Border thickness
                .attr("r", d => { // Circle radius
                    if (d.type === "c_type") return 15;
                    return 12;
                })
                .attr("fill", d => { // Circle colour
                    if (d.type === "c_type") return "blue";
                    if (d.type === "ck") return "orange";
                    if (d.type === "statid") return "green";
                    if (d.type === "hip") return "purple";
                    if (d.type === "hh") return "red";
                    if (d.type === "org") return "green";
                    return "gray";
                });

            // Add icons inside the circles
            node.append("text")
                .attr("class", "fa")  // Font Awesome class for styling
                .attr("font-family", "FontAwesome")
                .attr("text-anchor", "middle")
                .attr("x", 0) // Adjust horizontal position
                .attr("y", 5)  // Adjust vertical position
                .attr("fill", "white")
                .attr("font-size", d => (d.type === "prsn" ? "14px" : d.type === "statid" ? "11px" : "13px"))
                .text(d => {
                    if (d.type === "c_type") return "\uf007"; // person_icon
                    if (d.type === "ck") return "\uf564"; // cookie icon
                    if (d.type === "statid") return "\uf390"; // statid icon
                    if (d.type === "hip") return "\uf6ff"; // network icon
                    if (d.type === "hh") return "\uf015"; // house icon
                    if (d.type === "org") return "\ue533"; // org icon
                    return "\u003f"; // unknown icon
                });

            // Tooltip for nodes
            node.on("mouseover", (event, d) => {
                const tooltip = document.createElement("div");
                tooltip.innerHTML = `<strong>Type: ${d.type}</strong><br>Domain: ${d.domain}<br>Key: ${d.key}`;
                tooltip.className = "olap-graph-tooltip";
                
                // Get the actual node element's screen position (accounts for zoom/pan)
                const nodeElement = event.currentTarget;
                const nodeRect = nodeElement.getBoundingClientRect();
                
                // Position tooltip near the node
                const tooltipX = nodeRect.left + nodeRect.width + 10; // 10px to the right of node
                const tooltipY = nodeRect.top - 10; // 10px above node
                
                tooltip.style.left = tooltipX + "px";
                tooltip.style.top = tooltipY + "px";
                document.body.appendChild(tooltip);
            });
            node.on("mouseout", () => {
                document.querySelectorAll(".olap-graph-tooltip").forEach(tooltip => tooltip.remove());
            });

            // Update positions on simulation tick
            this.simulation.on("tick", () => {
                link
                    .attr("x1", d => d.source.x)
                    .attr("y1", d => d.source.y)
                    .attr("x2", d => d.target.x)
                    .attr("y2", d => d.target.y);
                node.attr("transform", d => `translate(${d.x},${d.y})`);
            });
        }
    },

    beforeUnmount() {
        if (this.simulation) {
            this.simulation.stop();
        }
    },

    mounted() {
        this.getDatabases()
    }
}
</script>

<style>
.olap-graph-tooltip {
    position: absolute;
    background-color: #6E84A3;
    color: #fff;
    padding: 8px;
    border-radius: 4px;
    font-size: 12px;
    pointer-events: none;
}
.olap-graph-container {
    flex: 1; /* Fills the entire card */
    width: 100%;
    height: 100%;
    border: 1px solid #ddd;
    background-color: #fff;
}
</style>
